import { useState, useMemo, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { countries, COUNTRY_CODE_ISO_2 } from "@/constants/countries.constants";
import { CDN_URL } from "@/config";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface PhoneInputProps {
  countryValue: string;
  phoneValue: string;
  onCountryChange: (iso: COUNTRY_CODE_ISO_2, code: string) => void;
  onPhoneChange: (value: string) => void;
  maxLength?: number;
  readOnly?: boolean;
  className?: string;
}

const sortedCountries = [...countries].sort((a, b) => a.name.localeCompare(b.name));

const getFlagUrl = (iso: string) =>
  `${CDN_URL}/flags/${iso.toLowerCase()}.svg`;

export const PhoneInput = ({
  countryValue,
  phoneValue,
  onCountryChange,
  onPhoneChange,
  maxLength = 15,
  readOnly = false,
  className,
}: PhoneInputProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = sortedCountries.find((c) => c.country === countryValue);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return sortedCountries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.includes(q) ||
        c.country.toLowerCase().includes(q)
    );
  }, [search]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      const c = filtered[highlightedIndex];
      onCountryChange(c.country, c.code);
      setOpen(false);
      setSearch("");
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={cn("flex", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="rounded-r-none border-slate-600 bg-slate-800/50 text-white hover:bg-slate-700 px-2 gap-1 min-w-[90px]"
          >
            {selected ? (
              <img
                src={getFlagUrl(selected.country)}
                alt={selected.country}
                className="w-5 h-4 object-cover rounded-sm shrink-0"
              />
            ) : (
              <span className="text-slate-400 text-xs w-5">–</span>
            )}
            <span className="text-sm font-mono">
              {selected ? `${selected.country} ${selected.code}` : "Select"}
            </span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-72 p-0 bg-slate-900 border-slate-700"
          align="start"
        >
          {/* Search */}
          <div className="flex items-center border-b border-slate-700 px-3">
            <Search className="h-4 w-4 text-slate-400 mr-2 shrink-0" />
            <input
              autoFocus
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full py-2 bg-transparent text-sm text-white placeholder:text-slate-400 outline-none"
            />
          </div>

          {/* List */}
          <ul
            ref={listRef}
            className="max-h-60 overflow-y-auto py-1"
            onWheelCapture={(e) => e.stopPropagation()}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">No results found</li>
            ) : (
              filtered.map((c, index) => (
                <li
                  key={c.country}
                  onClick={() => {
                    onCountryChange(c.country, c.code);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 cursor-pointer text-sm text-white hover:bg-slate-700",
                    c.country === countryValue && "bg-slate-700",
                    index === highlightedIndex && "bg-slate-600 outline outline-1 outline-slate-500"
                  )}
                >
                  <img
                    src={getFlagUrl(c.country)}
                    alt={c.country}
                    className="w-5 h-4 object-cover rounded-sm shrink-0"
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-slate-400 font-mono text-xs shrink-0">
                    {c.code}
                  </span>
                </li>
              ))
            )}
          </ul>
        </PopoverContent>
      </Popover>

      <Input
        type="tel"
        inputMode="numeric"
        value={phoneValue}
        readOnly={readOnly}
        maxLength={maxLength}
        onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ""))}
        className="rounded-l-none bg-slate-800/50 border-slate-600 text-white"
        placeholder="Enter phone number"
      />
    </div>
  );
};